import { getEventFromValue, PublicLead } from "shared/topchef.types";
import LeadStatusDetails from "pages/shared/lead-status/LeadStatusDetails"
import { useLocation } from "react-router";
import { useContext, useEffect, useState } from "react"
import { NotificationContext } from "shared/NotificationContext"
import { useParams } from "react-router"
//import { useErrorBoundary } from "react-error-boundary"
import { Lead, NotificationType } from "shared/topchef.types"
import backofficeApi from 'shared/topchef-api-backoffice';
import { useTranslation } from "react-i18next";

type PublicLeadContentProps = {
  publicLead: PublicLead;
};

const PublicLeadContent = ({ publicLead }: PublicLeadContentProps) => {
    const { t, i18n } = useTranslation();

    const formattedBudget = (typeof publicLead.budget === 'number' && publicLead.budget > 0) ? publicLead.budget : t('lead.unspecified');
  
  const notificationContext = useContext(NotificationContext);
  const { triggerNotification } = notificationContext;

  const params = useParams();
  const leadId: string = params.leadId || '';

  const [isReady, setReady] = useState<boolean>(false);
  const [lead, setLead] = useState<Lead | null>(null);

  //const { showBoundary } = useErrorBoundary();

  const location = useLocation();
  const isBackoffice = location.pathname.startsWith("/backoffice");


  useEffect(() => { // eslint-disable-line react-hooks/exhaustive-deps
        async function loadLead() {
            try {
                const lead: Lead = await backofficeApi.loadLeadDetails(leadId);
                setLead(lead);
                setReady(true);
            } catch (error: any) { 
                const newError = new Error(t('apiErrors.loadLead', { details: error.message }) as string);
                triggerNotification({type: NotificationType.error, message : newError.message}); 
                //showBoundary(newError);
                // Ignore error to avoid error loop
                setReady(true);
            } 
        }
        
        if (isReady) {
            return;
        }

        isBackoffice && loadLead();

  }, [isReady, isBackoffice, leadId, triggerNotification]); // eslint-disable-line react-hooks/exhaustive-deps


  return (
      <div className="row mt-4">

        {lead && (
        <>
            <div className="d-flex flex-row justify-content-center gap-3 mb-5">
                <div className="p-3 border rounded shadow-sm bg-white col-sm-3 lead-followers">
                    <h6 className="text-lg fw-bold mb-2 block text-center">{t('lead.positionedPlaces')}</h6>
                            {lead.followers && lead.followers.length === 0 ? (
                                <p>{t('lead.none')}</p>
                            ) : (
                                <ul className="space-y-2">
                                {lead.followers && lead.followers.map((follower) => (
                                    <li className="flex items-center gap-2" key={follower.place.id}>
                                    {follower.place.name}
                                    </li>
                                ))}
                                </ul>
                            )}
                </div>
            </div>
        </>
        )}

        <h3 className="mb-3 text-center">{t('lead.briefDetails')}</h3>

        <div className="p-3 pt-2 pb-2 text-center mb-0">
            <span id="lead_creation_date_label" className="fw-bold">{t('lead.depositDate')}</span>
            <span id="lead_creation_date_value" className="creation-date">
                {publicLead.creationDate
                    ? (() => {
                        const languageLocale = i18n.language === 'es' ? 'es-ES' : i18n.language === 'en' ? 'en-US' : 'fr-FR';
                        return new Date(publicLead.creationDate).toLocaleString(languageLocale, {
                          day: "2-digit",
                          month: "long",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        });
                      })()
                    : t('lead.notProvided')}
            </span>
        </div>

        <div className="mb-4 text-center">
            <LeadStatusDetails lead={publicLead} />
        </div>
         
        {lead && lead.status === "signed" && (
                <>
                    {lead.winPlace ? (
                        <p className="text-green-600 text-center signed-brief-label">{t('lead.signedBriefWith', { name: lead.winPlace.name })}</p>
                    ) : (
                        <p className="text-green-600 text-center signed-brief-label">{t('lead.signedBrief')}</p>
                    )}
                </>
        )}
       

        <div className="mb-3 col-md-6">
            <strong>{t('lead.eventDate')}</strong>
            <span id="display_event_date" className="card p-1 bg-light">{publicLead.targetDate.toString()}</span>
        </div>
        <div className="mb-3 col-md-6">
            <strong>{t('lead.eventType')}</strong>
            <span id="display_event_type" className="card p-1 bg-light">{t(getEventFromValue(publicLead.type!).label)}</span>
        </div>
        {(publicLead.startTime !=='' && publicLead.endTime !=='') &&
            <>
                <div className="mb-3 col-md-6">
                    <strong>{t('lead.startTime')}</strong>
                    <span id="display_start_time" className="card p-1 bg-light">{publicLead.startTime}</span>
                </div>
                <div className="mb-3 col-md-6">
                    <strong>{t('lead.endTime')}</strong>
                    <span id="display_end_time" className="card p-1 bg-light">{publicLead.endTime}</span>
                </div>
            </>
        }
        <div className="mb-3 col-md-6">
            <strong>{t('lead.guestsCount')}</strong>
            <span id="display_guests" className="card p-1 bg-light">{publicLead.guests}</span>
        </div>
        <div className="col-md-6">
            <strong>{t('lead.totalBudget')}</strong>
            <span id="display_budget" className="card p-1 bg-light">{formattedBudget}</span>
        </div>
        <div className="mb-3 col-md-6">
            <strong>{t('lead.city')}</strong>
            <span id="display_zipcode" className="card p-1 bg-light">{publicLead.city}</span>
        </div>
        <div className="mb-3 col-md-6">
            <strong>{t('lead.company')}</strong>
            <span id="display_company" className="card p-1 bg-light">{publicLead.contactCompany}</span>
        </div>
        <div className="mb-3 col-md-12">
            <strong>{t('lead.eventDetails')}</strong>
            <p id="display_description" className="card p-1 bg-light" style={{whiteSpace: 'pre-line'}}>{publicLead.description} </p>
        </div>
    </div>
  );

}

export default PublicLeadContent;
